'use client';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useEffect, forwardRef, useContext } from 'react';

const TabContext = /*#__PURE__*/React__default.createContext({
  activeTab: '',
  setActiveTab: name => {
  }
});
const Tabs = _ref => {
  let {
    initialTab,
    children
  } = _ref;
  const [activeTab, setActiveTab] = useState(initialTab);
  return /*#__PURE__*/React__default.createElement(TabContext.Provider, {
    value: {
      activeTab,
      setActiveTab
    }
  }, children);
};
Tabs.displayName = 'Tabs';
const TabList = _ref2 => {
  let {
    children,
    borderTop
  } = _ref2;
  const [isAtEnd, setIsAtEnd] = useState(false);
  const ref = useRef(null);
  const handleScroll = e => {
    const element = e.target;
    setIsAtEnd(element.scrollWidth - element.scrollLeft === element.clientWidth);
  };
  const checkIsAtEnd = () => {
    if (ref.current) {
      setIsAtEnd(ref.current.scrollWidth - ref.current.scrollLeft <= ref.current.clientWidth);
    }
  };
  // Run on mount and whenever the window is resized
  useEffect(() => {
    checkIsAtEnd();
    window.addEventListener('resize', checkIsAtEnd);
    // Cleanup function
    return () => {
      window.removeEventListener('resize', checkIsAtEnd);
    };
  }, []);
  return /*#__PURE__*/React__default.createElement("div", {
    className: `b-tabs ${isAtEnd ? '' : 'b-tabs--gradient'}`
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: ref,
    role: "tablist",
    onScroll: handleScroll,
    className: `b-tabs__list ${borderTop ? 'b-tabs__list--top-border' : 'b-tabs__list--bottom-border'}`,
    "data-testid": "tablist"
  }, React__default.Children.map(children, child => {
    if ( /*#__PURE__*/React__default.isValidElement(child)) {
      return /*#__PURE__*/React__default.cloneElement(child, {
        ...child.props,
        borderTop
      });
    }
    return child;
  })));
};
TabList.displayName = 'TabList';
const Tab = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    name,
    children,
    borderTop,
    className,
    ...rest
  } = _ref3;
  const {
    activeTab,
    setActiveTab
  } = useContext(TabContext);
  return /*#__PURE__*/React__default.createElement("button", _extends({
    ref: ref,
    role: "tab",
    "aria-selected": activeTab === name,
    "aria-controls": `panel-${name}`,
    className: `${className} b-tab ${borderTop ? 'b-tab--top-border' : 'b-tab--bottom-border'} ${activeTab === name ? 'b-tab--active' : ''}`,
    onClick: () => setActiveTab(name),
    "data-testid": `tab-${name}`
  }, rest), children);
});
Tab.displayName = 'Tab';
const TabContent = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
    for: name,
    children
  } = _ref4;
  const {
    activeTab
  } = useContext(TabContext);
  return activeTab === name ? /*#__PURE__*/React__default.createElement("div", {
    ref: ref,
    id: `panel-${name}`,
    role: "tabpanel",
    "aria-labelledby": `tab-${name}`,
    "data-testid": `tabpanel-${name}`
  }, children) : null;
});
TabContent.displayName = 'TabContent';

export { Tab, TabContent, TabList, Tabs };
